import { userApiClient, ApiError } from '../api/client';
import { userSession } from '@/lib/session';
import type { User, Organization } from '../../types';
import { passwordResetService } from './PasswordResetService';
import type { SwitchOrganizationResponse } from './OrganizationService';
import {
  registrationService,
  type RegisterData as RegistrationRegisterData,
  type SignupData as RegistrationSignupData,
  type SignupResponse as RegistrationSignupResponse,
  type SignupConfig as RegistrationSignupConfig,
} from './RegistrationService';
import {
  organizationService,
  type UserOrganization as OrgServiceUserOrganization,
  type UserOrganizationsResponse as OrgServiceUserOrganizationsResponse,
} from './OrganizationService';

export interface Session {
  user: User;
  organization: Organization | null;
  expires_at?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
  /**
   * Six-digit TOTP code or 10-character recovery code. Required when the
   * user has a verified MFA factor; left undefined on first attempt so
   * the backend can reply with `400 mfa_required`.
   */
  otp?: string;
}

export type RegisterData = RegistrationRegisterData;
export type SignupData = RegistrationSignupData;
export type SignupResponse = RegistrationSignupResponse;
export type SignupConfig = RegistrationSignupConfig;

export type UserOrganization = OrgServiceUserOrganization;
export type UserOrganizationsResponse = OrgServiceUserOrganizationsResponse;

export interface UserState {
  onboardingCompleted: boolean;
  emailVerified: boolean;
  hasOrganizations: boolean;
  termsAccepted: boolean;
  pendingMembershipsCount: number;
  profileComplete: boolean;
}

export interface LoginResponse {
  user: {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    roles?: string[];
    permissions?: string[];
  };
  organization: Organization | null;
  userState?: UserState;
  requiresOrgSelection?: boolean;
  organizations?: Array<{ id: string; name: string; slug: string }>;
  expiresIn?: number;
}

class AuthService {
  private currentUser: User | null = null;
  private onAuthStateChange?: (user: User | null) => void;

  setAuthStateChangeHandler(handler: (user: User | null) => void) {
    this.onAuthStateChange = handler;
  }

  async login(credentials: LoginCredentials & { organizationId?: string }): Promise<LoginResponse> {
    const options: RequestInit & {
      skipAuth?: boolean;
      headers?: Record<string, string>;
    } = { skipAuth: true };

    if (credentials.organizationId) {
      options.headers = { 'X-Tenant-ID': credentials.organizationId };
    }

    try {
      const response = await userApiClient.post<LoginResponse>(
        '/api/v1/auth/secure/login',
        {
          email: credentials.email,
          password: credentials.password,
          remember_me: credentials.rememberMe ?? false,
          otp: credentials.otp,
        },
        options,
      );

      userSession.loginCompleted(response.expiresIn ?? 900);

      this.currentUser = {
        id: response.user.id,
        email: response.user.email,
        displayName: response.user.displayName || undefined,
        firstName: response.user.firstName || undefined,
        lastName: response.user.lastName || undefined,
        roles: response.user.roles || [],
        permissions: response.user.permissions || [],
      } as User;
      this.onAuthStateChange?.(this.currentUser);

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Login failed', 0, 'UnknownError', error);
    }
  }

  async selectOrganization(
    organizationId: string,
  ): Promise<{ success: boolean; organization: Organization }> {
    try {
      return await userApiClient.post<{ success: boolean; organization: Organization }>(
        '/api/v1/user/select-organization',
        { organizationId },
      );
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Organization selection failed', 0, 'UnknownError', error);
    }
  }

  async fetchUserOrganizations(): Promise<Organization[]> {
    try {
      const response = await userApiClient.get<{ organizations: Organization[] }>(
        '/api/v1/user/organizations',
      );
      return response.organizations;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to fetch organizations', 0, 'UnknownError', error);
    }
  }

  async register(data: RegisterData): Promise<User> {
    const user = await registrationService.register(data);
    this.currentUser = user;
    this.onAuthStateChange?.(user);
    return user;
  }

  async signup(data: SignupData): Promise<SignupResponse> {
    return registrationService.signup(data);
  }

  async resendVerificationEmail(email: string): Promise<{ success: boolean; message: string }> {
    return registrationService.resendVerificationEmail(email);
  }

  async verifyOtp(email: string, token: string): Promise<void> {
    return registrationService.verifyOtp(email, token);
  }

  async getSignupConfig(): Promise<SignupConfig> {
    return registrationService.getSignupConfig();
  }

  async getUserOrganizations(
    includePending: boolean = true,
    options: { includeRoles?: boolean } = {},
  ): Promise<UserOrganizationsResponse> {
    return organizationService.getUserOrganizations(includePending, options);
  }

  async logout(): Promise<void> {
    try {
      await userApiClient.post('/api/v1/auth/secure/logout', {}, { skipAuth: false });
    } catch {
      // Always clear local state, even on network failure.
    } finally {
      userSession.logoutCompleted();
      localStorage.removeItem('rememberMe');
      this.currentUser = null;
      this.onAuthStateChange?.(null);
    }
  }

  /**
   * Resolve the current session.
   *
   * If `prefetchedUser` is provided (including `null`), it's used as the
   * /auth/me response without hitting the network — this is the boot-time
   * path where AuthContext has already called `userSession.probe()` and
   * the probe returned the same body. Pass `undefined` to fetch fresh.
   *
   * Either path sets `this.currentUser` and fires the auth-state-change
   * callback so service-layer consumers stay in sync regardless of how
   * the user was obtained.
   */
  async getCurrentSession(prefetchedUser?: User | null): Promise<Session | null> {
    let user: User | null;
    if (prefetchedUser !== undefined) {
      user = prefetchedUser;
    } else {
      try {
        user = await userApiClient.get<User | null>('/api/v1/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          this.currentUser = null;
          this.onAuthStateChange?.(null);
          return null;
        }
        throw error;
      }
    }

    if (user === null) {
      this.currentUser = null;
      this.onAuthStateChange?.(null);
      return null;
    }

    this.currentUser = user;
    this.onAuthStateChange?.(user);

    const org: Organization | null = user.currentOrganization
      ? {
          id: user.currentOrganization.id,
          name: user.currentOrganization.name,
          slug: user.currentOrganization.slug ?? undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      : null;

    return { user, organization: org, expires_at: undefined };
  }

  async switchOrganization(organizationId: string): Promise<SwitchOrganizationResponse> {
    return organizationService.switchOrganization(organizationId);
  }

  async resetPasswordRequest(email: string): Promise<void> {
    return passwordResetService.requestReset(email);
  }

  async setPassword(password: string, token: string): Promise<void> {
    return passwordResetService.setNewPassword(password, token);
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Replace the service-layer currentUser pointer. Used by the org-switch
   * flow to keep authService consumers (e.g. logger context) in sync after
   * the new session is applied in-place — without a /auth/me round-trip.
   */
  setCurrentUser(user: User | null): void {
    this.currentUser = user;
    this.onAuthStateChange?.(user);
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }
}

export const authService = new AuthService();
